// src/components/TemplateCreatePage.jsx
//
// Configuration > Mail Configuration > Template > "Add Template" --
// Screen 1 of the requested two-screen flow ("Template Screen 1 --
// Create / upload template"). Screen 2 (the list of existing templates,
// with Edit/Delete/Use Template) is EmailConfiguration.jsx's Template
// tab, which now links here instead of opening a create modal.
//
// Editing an existing template still happens in a modal on that same
// list page -- only creation got its own route, per the flow as
// described. Both share the exact same field markup via
// TemplateFormFields.jsx so there's one place that defines what a
// template's fields are, not two that can drift apart.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api as axios, API_ORIGIN } from "../config/apiConfig";
import TemplateFormFields, { emptyTemplateForm } from "./TemplateFormFields";
import { ROUTES } from "../config/routes";

const API_BASE = API_ORIGIN;

export default function TemplateCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyTemplateForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const goBack = () => navigate(ROUTES.CONFIG_MAIL);

  const handleCreate = async () => {
    setFormError("");
    if (!form.Template_Code.trim()) {
      setFormError("Template Code is required (a stable internal name, e.g. CET_WELCOME_EMAIL).");
      return;
    }
    if (!form.Template_Name.trim()) {
      setFormError("Template Name is required.");
      return;
    }
    if (!form.Subject.trim()) {
      setFormError("Subject is required.");
      return;
    }
    if (!form.Body_Html.trim()) {
      setFormError("Body is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        Template_Code: form.Template_Code.trim(),
        Template_Name: form.Template_Name.trim(),
        Template_Description: form.Template_Description.trim(),
        Template_Type: form.Template_Type.trim(),
        Version: form.Version.trim() || "1.0",
        Recipient_To: form.Recipient_To,
        Recipient_CC: form.Recipient_CC.trim(),
        Recipient_BCC: form.Recipient_BCC.trim(),
        Subject: form.Subject.trim(),
        Body_Html: form.Body_Html,
        Placeholders: form.Placeholders.trim(),
        Is_Active: form.Is_Active
      };

      const res = await axios.post(`${API_BASE}/mail-templates`, payload);
      const data = res?.data;
      if (!data || data.success !== true) {
        setFormError(data?.error || "Failed to create template.");
        setSaving(false);
        return;
      }

      setSaving(false);
      goBack();
    } catch (err) {
      console.error("Create mail template failed:", err);
      setFormError(err?.response?.data?.error || "Failed to create template.");
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-title-head d-flex align-items-center mb-3">
        <div className="flex-grow-1">
          <h4 className="page-main-title m-0">Create Email Template</h4>
          <p className="text-muted fs-13 mb-0">Configuration &gt; Mail Configuration &gt; Template &gt; New</p>
        </div>
        <button type="button" className="btn btn-light" onClick={goBack}>
          <i className="ti ti-arrow-left me-1" />Back to Templates
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-3 p-md-4">
          {formError && <div className="alert alert-danger py-2 px-3 mb-3">{formError}</div>}
          <TemplateFormFields form={form} setForm={setForm} isEdit={false} showPreview={showPreview} setShowPreview={setShowPreview} />
        </div>
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-light" onClick={goBack}>Cancel</button>
          <button type="button" className="btn btn-primary px-4" onClick={handleCreate} disabled={saving}>
            {saving ? "Creating..." : "Create Template"}
          </button>
        </div>
      </div>
    </>
  );
}
